import { useState, useEffect, useRef } from "react";
import {
  Box,
  Menu,
  Typography,
  Divider,
  Button,
  CircularProgress,
  List,
  ListItem,
  ListItemButton,
} from "@mui/material";
import {
  Notifications as NotificationsIcon,
  Event as EventIcon,
  Description,
  Schedule,
  Cancel,
  LocationOn,
  Store,
} from "@mui/icons-material";
import { observer, useLocalObservable } from "mobx-react-lite";
import { useNotificationContext } from "@root/contexts/NotificationContext";
import { NotificationDto } from "@root/utils/dtos";
import { NotificationType } from "@root/utils/enums";
import { navigate } from "vike/client/router";
import { ModalStore } from "@root/stores/modalStore";
import { eventService } from "@root/services/api/eventService";
import { organizationService } from "@root/services/api/organizationService";
import { ViewEventModal } from "@root/components/modals/ViewEventModal";
import { ViewOrganizationModal } from "@root/components/modals/ViewOrganizationModal";
import { Event, Organization } from "@root/utils/entities";
import {
  hasErrors,
  getApiError,
  parseEventFromBackend,
} from "@root/utils/functions";
import React from "react";

interface NotificationDropdownProps {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
}

const getNotificationIcon = (type: number) => {
  switch (type) {
    case NotificationType.OrganizationEventCreated:
      return <EventIcon sx={{ fontSize: "1.125rem", color: "#4CAF50" }} />;
    case NotificationType.EventDescriptionChanged:
      return <Description sx={{ fontSize: "1.125rem", color: "#2196F3" }} />;
    case NotificationType.EventDateTimeChanged:
      return <Schedule sx={{ fontSize: "1.125rem", color: "#FF9800" }} />;
    case NotificationType.EventCancelled:
      return <Cancel sx={{ fontSize: "1.125rem", color: "#F44336" }} />;
    case NotificationType.VenueEventAdded:
      return <LocationOn sx={{ fontSize: "1.125rem", color: "#009688" }} />;
    case NotificationType.VendorEventAdded:
      return <Store sx={{ fontSize: "1.125rem", color: "#9C27B0" }} />;
    default:
      return <NotificationsIcon sx={{ fontSize: "1.125rem" }} />;
  }
};

export const NotificationDropdown = observer(
  ({ anchorEl, open, onClose }: NotificationDropdownProps) => {
    const {
      notifications,
      unreadCount,
      loading,
      markAsRead,
      markAllAsRead,
      loadNotifications,
    } = useNotificationContext();
    const [displayedNotifications, setDisplayedNotifications] = useState<
      NotificationDto[]
    >([]);
    const menuRef = useRef<HTMLDivElement>(null);

    // Modal stores and state
    const eventModalStore = useLocalObservable(() => new ModalStore());
    const organizationModalStore = useLocalObservable(() => new ModalStore());
    const [event, setEvent] = useState<Event | undefined>(undefined);
    const [organization, setOrganization] = useState<Organization | undefined>(
      undefined
    );
    const [markingAllAsRead, setMarkingAllAsRead] = useState(false);

    // Load all notifications when dropdown opens
    useEffect(() => {
      if (open) {
        loadNotifications(1, 20);
      }
    }, [open, loadNotifications]);

    useEffect(() => {
      setDisplayedNotifications(notifications);
    }, [notifications]);

    const handleNotificationClick = async (notification: NotificationDto) => {
      onClose();

      if (!notification.isRead) {
        markAsRead(notification.id);
      }

      if (notification.eventId) {
        try {
          const response = await eventService.getEvent(notification.eventId);
          if (response?.data && !hasErrors(response)) {
            const parsedEvent = parseEventFromBackend(response.data.event);
            setEvent(parsedEvent);
            eventModalStore.open();
          } else {
            console.error("Error loading event:", getApiError(response));
          }
        } catch (error) {
          console.error("Error loading event:", error);
        }
      } else if (notification.organizationId) {
        try {
          const response = await organizationService.getOrganization(
            notification.organizationId
          );
          if (response?.data && !hasErrors(response)) {
            setOrganization(response.data.organization);
            organizationModalStore.open();
          } else {
            console.error("Error loading organization:", getApiError(response));
          }
        } catch (error) {
          console.error("Error loading organization:", error);
        }
      }
    };

    const handleCloseEventModal = () => {
      eventModalStore.close();
      setEvent(undefined);
    };

    const handleCloseOrganizationModal = () => {
      organizationModalStore.close();
      setOrganization(undefined);
    };

    const handleViewOrganization = (org: Organization) => {
      eventModalStore.close();
      setEvent(undefined);
      setOrganization(org);
      organizationModalStore.open();
    };

    const handleViewEvent = (evt: Event) => {
      organizationModalStore.close();
      setOrganization(undefined);
      setEvent(evt);
      eventModalStore.open();
    };

    const handleMarkAllAsRead = async () => {
      setMarkingAllAsRead(true);
      try {
        await markAllAsRead();
        await loadNotifications(1, 20);
      } catch (err) {
        console.error("Error marking all as read:", err);
      } finally {
        setMarkingAllAsRead(false);
      }
    };


    return (
      <>
        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={onClose}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "right",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
          PaperProps={{
            sx: {
              width: { xs: "90vw", sm: "420px" },
              maxWidth: "420px",
              maxHeight: "600px",
              mt: 1.5,
              borderRadius: "12px",
              boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
              border: "1px solid rgba(0,0,0,0.08)",
              overflow: "hidden",
            },
          }}
          ref={menuRef}
        >
          <Box
            sx={{
              p: 0.75,
              pb: 0.5,
              borderBottom: "1px solid rgba(0,0,0,0.08)",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  fontSize: "0.875rem",
                  color: "#212121",
                }}
              >
                Notifications
              </Typography>
              <Button
                size="small"
                onClick={handleMarkAllAsRead}
                disabled={markingAllAsRead || unreadCount === 0}
                sx={{
                  textTransform: "none",
                  fontSize: "0.6875rem",
                  minWidth: "auto",
                  padding: "0.25rem 0.625rem",
                  color: "white",
                  backgroundColor: "#ff0000",
                  fontWeight: 500,
                  borderRadius: "4px",
                  height: "24px",
                  "&:hover": {
                    backgroundColor: "#e60000",
                  },
                  "&:disabled": {
                    backgroundColor: "#9e9e9e",
                    color: "white",
                    filter: "grayscale(100%)",
                    cursor: "not-allowed",
                  },
                }}
              >
                {markingAllAsRead ? (
                  <CircularProgress size={12} sx={{ color: "white" }} />
                ) : (
                  "Mark all as read"
                )}
              </Button>
            </Box>
          </Box>

          <Box
            sx={{
              maxHeight: "500px",
              overflowY: "auto",
              overflowX: "hidden",
              "&::-webkit-scrollbar": {
                width: "6px",
              },
              "&::-webkit-scrollbar-track": {
                background: "transparent",
              },
              "&::-webkit-scrollbar-thumb": {
                background: "rgba(0,0,0,0.2)",
                borderRadius: "3px",
                "&:hover": {
                  background: "rgba(0,0,0,0.3)",
                },
              },
            }}
          >
            {loading && displayedNotifications.length === 0 ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  py: 5,
                }}
              >
                <CircularProgress size={28} sx={{ color: "#ff0000" }} />
              </Box>
            ) : displayedNotifications.length === 0 ? (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  py: 5,
                  px: 2,
                }}
              >
                <NotificationsIcon
                  sx={{
                    fontSize: "4rem",
                    color: "#bdbdbd",
                    mb: 1.5,
                    opacity: 0.6,
                  }}
                />
                <Typography
                  variant="body2"
                  sx={{
                    color: "#757575",
                    fontSize: "0.9375rem",
                    fontWeight: 500,
                  }}
                >
                  No notifications
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: "#9e9e9e",
                    fontSize: "0.8125rem",
                    mt: 0.5,
                  }}
                >
                  You&apos;re all caught up!
                </Typography>
              </Box>
            ) : (
              <List sx={{ p: 0 }}>
                {displayedNotifications.map((notification) => (
                  <ListItem
                    key={notification.id}
                    disablePadding
                    sx={{
                      backgroundColor: notification.isRead
                        ? "#fafafa"
                        : "rgba(25, 118, 210, 0.06)",
                      borderLeft: notification.isRead
                        ? "none"
                        : "4px solid #1976d2",
                      borderBottom: "1px solid rgba(0,0,0,0.06)",
                      transition: "background-color 0.2s",
                      "&:hover": {
                        backgroundColor: notification.isRead
                          ? "rgba(0,0,0,0.02)"
                          : "rgba(25, 118, 210, 0.1)",
                      },
                      "&:last-child": {
                        borderBottom: "none",
                      },
                    }}
                  >
                    <ListItemButton
                      onClick={() => handleNotificationClick(notification)}
                      sx={{
                        py: 1,
                        px: 1.5,
                        gap: 0.75,
                        "&:hover": {
                          backgroundColor: "transparent",
                        },
                      }}
                    >
                      <Box
                        sx={{
                          mt: 0.125,
                          display: "flex",
                          alignItems: "flex-start",
                          flexShrink: 0,
                        }}
                      >
                        {getNotificationIcon(notification.type)}
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "flex-start",
                            justifyContent: "space-between",
                            gap: 0.75,
                            mb: 0.375,
                          }}
                        >
                          <Typography
                            variant="subtitle2"
                            sx={{
                              fontWeight: notification.isRead ? 500 : 700,
                              fontSize: "0.8125rem",
                              color: notification.isRead
                                ? "#616161"
                                : "#212121",
                              lineHeight: 1.3,
                              flex: 1,
                            }}
                          >
                            {notification.title}
                          </Typography>
                        </Box>
                        <Typography
                          variant="body2"
                          sx={{
                            color: notification.isRead ? "#757575" : "#424242",
                            fontSize: "0.8125rem",
                            wordBreak: "break-word",
                            lineHeight: 1.4,
                          }}
                        >
                          {notification.message}
                        </Typography>
                      </Box>
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            )}
          </Box>

          {displayedNotifications.length > 0 && (
            <>
              <Divider sx={{ my: 0 }} />
              <Box
                sx={{
                  p: 1.5,
                  display: "flex",
                  justifyContent: "center",
                  borderTop: "1px solid rgba(0,0,0,0.08)",
                  backgroundColor: "#fafafa",
                }}
              >
                <Button
                  variant="contained"
                  onClick={() => {
                    navigate("/account/notifications");
                    onClose();
                  }}
                  sx={{
                    textTransform: "none",
                    fontSize: "0.8125rem",
                    fontWeight: 600,
                    padding: "0.5rem 1.25rem",
                    borderRadius: "8px",
                    backgroundColor: "#ff0000",
                    boxShadow: "none",
                    "&:hover": {
                      backgroundColor: "#e60000",
                      boxShadow: "0 2px 8px rgba(255, 0, 0, 0.3)",
                    },
                  }}
                >
                  View all notifications
                </Button>
              </Box>
            </>
          )}
        </Menu>

        {/* Event Modal */}
        {event && (
          <React.Suspense fallback={null}>
            <ViewEventModal
              event={event}
              store={eventModalStore}
              onClose={handleCloseEventModal}
              viewOrganization={handleViewOrganization}
              onViewEvent={handleViewEvent}
            />
          </React.Suspense>
        )}

        {/* Organization Modal */}
        {organization && (
          <React.Suspense fallback={null}>
            <ViewOrganizationModal
              store={organizationModalStore}
              organization={organization}
              onClose={handleCloseOrganizationModal}
              onViewEvent={handleViewEvent}
            />
          </React.Suspense>
        )}
      </>
    );
  }
);
